import type { TurnaroundTimeType } from '#shared/types/TurnaroundTimeType';

const isWeekend = (date: Date): boolean => {
    return date.getDay() === 6 || date.getDay() === 0;
};

const isPriorTo2PM = (date: Date): boolean => {
    // Get the date/time components in the America/Chicago timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago',
        hour12: false,
        weekday: 'short',
        hour: 'numeric',
    });

    const parts = formatter.formatToParts(date);
    const hour = parts.find((p) => p.type === 'hour');

    return hour !== undefined && Number(hour.value) < 14;
};

const addBusinessDays = (daysToAdd: number): Date => {
    const date = new Date(Date.now());

    if (!isWeekend(date) && !isPriorTo2PM(date)) {
        // if today is a business day and past 2PM, start from next day
        date.setDate(date.getDate() + 1);
    }

    // add days until daysToAdd reaches zero
    while (daysToAdd > 0) {
        date.setDate(date.getDate() + 1);

        const isBusinessDay = !isWeekend(date);

        // only decrement for business days
        if (isBusinessDay) {
            daysToAdd -= 1;
        }
    }

    return date;
};

const asSoonAsDateFormatted = (minimumDaysToTurnaround: number) => {
    const date = addBusinessDays(minimumDaysToTurnaround);

    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();

    const suffix =
        day % 10 === 1 && day !== 11
            ? 'st'
            : day % 10 === 2 && day !== 12
              ? 'nd'
              : day % 10 === 3 && day !== 13
                ? 'rd'
                : 'th';

    return `${dayOfWeek}. ${month} ${day}${suffix}`;
};

const formatDateShort = (date: Date) => {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
};

const formatDateMedium = (date: Date) => {
    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${dayOfWeek}. ${month} ${day}`;
};

const turnaroundDateRangeFormatted = (
    minimumDaysToTurnaround: number | null,
    maximumDaysToTurnaround: number | null,
    useShortFormatOverride = false,
): string => {
    if (minimumDaysToTurnaround === null || maximumDaysToTurnaround === null) {
        return '';
    }

    const formatDate = useShortFormatOverride
        ? formatDateShort
        : formatDateMedium;

    if (minimumDaysToTurnaround === maximumDaysToTurnaround) {
        return formatDate(addBusinessDays(minimumDaysToTurnaround));
    }

    return `${formatDate(addBusinessDays(minimumDaysToTurnaround))} - ${formatDate(addBusinessDays(maximumDaysToTurnaround))}`;
};

const parseRange = (
    turnaroundDateString: string | null,
): TurnaroundTimeType | null => {
    if (!turnaroundDateString) {
        return null;
    }

    const match = turnaroundDateString.match(/(\d+)\s*(?:-|–)\s*(\d+)|(\d+)/);

    if (!match) return null;

    if (match[1] && match[2]) {
        return {
            min: Number(match[1]),
            max: Number(match[2]),
        };
    }

    const num = Number(match[3]);
    return {
        min: num,
        max: num,
    };
};

export {
    formatDateMedium,
    formatDateShort,
    parseRange,
    turnaroundDateRangeFormatted,
};
